import { useParams } from 'react-router-dom';
import { useTelegramBackButton } from '../../../utils/BackButton';
import { useGetRecipt } from '../services/getRecipt';
import { Loader2 } from 'lucide-react';
import i18n, { t } from 'i18next';
import { format } from 'date-fns';
import { Card } from '../../../components/ui/card';
import { Separator } from '../../../components/ui/separator';
import { useGetOrderById } from '../services/getOrderById';
import { truncate } from '../../../utils/truncate';
import { formatPrice } from '../../../utils/getPrice';

/** «2026-10-05 14:32:13» → «05.10.2026 14:32»; не разобралось — как пришло. */
const formatOrderDate = (value: string) => {
  const date = new Date(value.replace(' ', 'T'));
  return Number.isNaN(date.getTime()) ? value : format(date, 'dd.MM.yyyy HH:mm');
};

const Row = ({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: React.ReactNode;
  valueClassName?: string;
}) => (
  <div className="flex justify-between gap-3">
    <span className="text-body-base-regular text-text-tertiary">{label}</span>
    <span className={valueClassName ?? 'text-body-base-regular'}>{value}</span>
  </div>
);

export const OrderRecipt = () => {
  const { orderId } = useParams();
  useTelegramBackButton(`/profile/orders/${orderId}`);
  const { order: orderItem, isLoading: isLoadingOrder } = useGetOrderById(
    orderId || ''
  );
  // Расчёт — тот же, что на экране заказа (orderDetailAdapter): итог из
  // Mindbox со всеми скидками, а не grand_total без них.
  const {
    order,
    positions,
    delivery_price,
    total_price,
    discount_amount,
    is_promocode_can_be_used,
    promocode_name,
  } = orderItem;
  const { receipt, isLoading } = useGetRecipt(orderId || '');
  // discount_amount — grand_total минус итог, туда попадают и баллы. Коины
  // в чеке пользователю не показываем, поэтому скидка одной строкой вместе с
  // ними — так сумма заказа, доставка и скидка сходятся с итогом.
  const discount = Math.max(discount_amount, 0);
  // Пояснение бэкенда приходит только на uz и ru.
  const receiptMessage = receipt?.message
    ? i18n.language === 'uz'
      ? receipt.message.uz
      : receipt.message.ru
    : '';
  // Части чека показываем независимо — что пришло, то и рисуем: бэкенд может
  // прислать QR вместе с success: false или вместе с сообщением о кешбэке.
  // Пустые поля адаптер превращает в 0 и '' — их за данные не считаем.
  const hasFiscal = Boolean(
    receipt.qr.receipt_no || receipt.qr.sign || receipt.qr.term_id
  );
  const hasQrImage = Boolean(receipt.image);

  if (isLoading || isLoadingOrder) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col space-y-4 p-4">
      <div className="flex items-center flex-col">
        <p className="text-display-sm">{t('check')}</p>
      </div>
      <div className="flex flex-col items-center space-y-1 text-center">
        <p className="text-body-base-semibold">
          {t('orderPrefix')}
          {order.id}
        </p>
        {order.created_on ? (
          <p className="text-body-sm-regular text-text-tertiary">
            {formatOrderDate(order.created_on)}
          </p>
        ) : null}
        {order.delivery_address_title ? (
          <p className="text-body-sm-regular text-text-tertiary">
            {order.delivery_address_title}
          </p>
        ) : null}
      </div>
      <Card className="flex flex-col p-4 overflow-y-auto ">
        <div className="space-y-3 py-3">
          {positions.map(item => (
            <div key={item.id} className="flex items-center space-x-3">
              <div className="flex items-center justify-between  w-full">
                <div>
                  <p className="text-body-base-regular">
                    {truncate(item.title, 25)}
                  </p>
                  <p className="text-body-sm-regular text-text-tertiary">
                    x{item.count}
                  </p>
                </div>
                <div className="flex flex-col items-end">
                  {/* {item.oldPrice && (
                            <p className="relative inline-block text-caption-sm-medium text-text-tertiary">
                              {formatPrice(item.oldPrice)} {order.currency}
                              <span className="absolute left-0 top-2 w-full h-[1px] bg-fill-danger rotate-[4deg] origin-top-right" />
                            </p>
                          )} */}
                  <p className="text-body-base-regular">
                    {formatPrice(Number(item.count) * Number(item.price))}{' '}
                    {t('sum')}
                  </p>
                </div>
              </div>
            </div>
          ))}
          <Separator />
          <Row
            label={t('amount-of-order')}
            value={`${formatPrice(Number(order.total_amount))} ${t('sum')}`}
          />
          <Row
            label={t('deliveryLabel')}
            value={`${formatPrice(delivery_price)} ${t('sum')}`}
          />
          {is_promocode_can_be_used && promocode_name ? (
            <Row label={t('promocode')} value={promocode_name} />
          ) : null}
          {discount > 0 ? (
            <Row
              label={t('discount')}
              value={`-${formatPrice(discount)} ${t('sum')}`}
              valueClassName="text-body-base-regular text-brand-green"
            />
          ) : null}
          <Separator />
          <Row
            label={t('totalLabel')}
            value={`${formatPrice(total_price)} ${t('sum')}`}
            valueClassName="text-body-base-semibold"
          />
        </div>
        {hasFiscal ? (
          <>
            <Separator className="mb-4" />
            <div className="flex flex-col space-y-4">
              <div className="flex flex-row items-center justify-between gap-3">
                <p>{t('receipt.id')}</p>
                <p>{receipt.qr.receipt_no}</p>
              </div>
              <div className="flex flex-row items-center justify-between gap-3">
                <p>{t('receipt.fiscal')}</p>
                <p className="break-all text-right">{receipt.qr.sign}</p>
              </div>
              <div className="flex flex-row items-center justify-between gap-3">
                <p>{t('receipt.terminal')}</p>
                <p>{receipt.qr.term_id}</p>
              </div>
            </div>
          </>
        ) : null}
        {hasQrImage ? (
          <>
            <Separator className="my-4" />
            <div className="flex items-center justify-center">
              <img
                src={receipt.image}
                alt="qr"
                className="max-w-28 max-h-28 object-cover"
                onClick={() => {
                  if (receipt.qr.url) window.Telegram.WebApp.openLink(receipt.qr.url);
                }}
              />
            </div>
          </>
        ) : null}
        {receiptMessage ? (
          <>
            <Separator className="my-4" />
            <p className="text-center text-body-sm-regular text-text-secondary">
              {receiptMessage}
            </p>
          </>
        ) : null}
      </Card>
    </div>
  );
};
